import ActionButton from "@/components/buttons/ActionButton";
import { SecondaryButton } from "@/components/buttons/SecondaryButton";
import AntIcon from "@/components/icons/AntIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import { formatPhoneNumber } from "@/utils/formatUtils";
import { URLQuery } from "@/utils/queryUtils";
import { Button, Collapse, Popconfirm, Space, Tag } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import {
  useDeleteClientUserAccount,
  useGetClientUserAccounts,
  useResendUserConfirmLink,
} from "../../../../common/api/queries";
import { Permission } from "../../../../common/security/enums";
import { UUID } from "../../../../typings/global";
import { useHasPermissions } from "../../../auth/hooks";
import { ClientUserAccountWithUserAgentView } from "../../../user/types";
import { ADD_ACCOUNT_QUERY_KEY } from "../../utils";
import { CreateClientUserAccountForm } from "../forms/accounts/CreateClientUserAccountForm";
import { UpdateClientUserAccountForm } from "../forms/accounts/UpdateClientUserAccountForm";

interface Props {
  clientId: UUID;
}

export const ClientUserAccountsView = (props: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // A link elsewhere on the client detail asks for the create form through the URL, also when this tab is mounted
  const addAccountRequested = searchParams.get(ADD_ACCOUNT_QUERY_KEY) === "true";

  const [createFormOpen, setCreateFormOpen] = useState(false);
  const [accountToUpdate, setAccountToUpdate] = useState<ClientUserAccountWithUserAgentView>();

  const hasClientAccountsPermission = useHasPermissions(Permission.CLIENT_ACCOUNTS);

  const { data: accounts } = useGetClientUserAccounts(hasClientAccountsPermission ? props.clientId : undefined);
  const resendUserConfirmLink = useResendUserConfirmLink();
  const deleteClientUserAccount = useDeleteClientUserAccount();

  const handleCreateFormCancel = () => {
    setCreateFormOpen(false);

    if (addAccountRequested) {
      navigate(
        {
          search: URLQuery.searchParamsToString({
            searchParams: { ...Object.fromEntries(searchParams), [ADD_ACCOUNT_QUERY_KEY]: undefined },
          }),
        },
        { replace: true },
      );
    }
  };

  return (
    <>
      {accounts?.length ? (
        <Collapse
          accordion
          items={accounts?.map((account) => ({
            key: account.id,
            label: `${account.user.name} | ${account.user.email || account.user.emailToConfirm}`,
            extra: (
              <>
                {account.user.locked && <Tag color="red">{t("user.helpers.lockedUser")}</Tag>}
                {account.disabled && <Tag color="purple">{t("user.helpers.disabledAccount")}</Tag>}
                {!account.confirmed && <Tag color="orange">{t("user.helpers.unconfirmedAccount")}</Tag>}

                <span onClick={(event) => event.stopPropagation()}>
                  <Button
                    className="margin-right-tiny"
                    type="primary"
                    size="small"
                    icon={<AntIcon type="edit" />}
                    onClick={() => setAccountToUpdate(account)}
                  >
                    {t("user.actions.updateAccount")}
                  </Button>
                  <Popconfirm
                    title={t("user.titles.deleteClientAccountConfirm")}
                    icon={<PopconfirmDeleteIcon />}
                    okText={t("common.yes")}
                    cancelText={t("common.no")}
                    okType="danger"
                    onConfirm={() => deleteClientUserAccount.mutate({ id1: props.clientId, id2: account.id })}
                  >
                    <ActionButton icon="minus">{t("user.actions.deleteAccount")}</ActionButton>
                  </Popconfirm>
                </span>
              </>
            ),
            children: (
              <table className="data-table-view margin-bottom-medium margin-top-medium">
                <tbody>
                  <tr>
                    <td>{t("user.attrs.name")}:</td>
                    <td>{account.user.name}</td>
                    <td>
                      {t("user.attrs.wrongLoginAttempts")}: {account.user.wrongLoginAttempts}
                    </td>
                  </tr>
                  <tr>
                    <td>{t("user.attrs.email")}:</td>
                    <td>{account.user.email || account.user.emailToConfirm}</td>
                    <td>
                      <Space>
                        {account.user.locked && <Tag color="red">{t("user.helpers.lockedUser")}</Tag>}

                        {account.disabled && <Tag color="purple">{t("user.helpers.disabledAccount")}</Tag>}

                        {!account.confirmed && (
                          <>
                            <Tag color="orange">{t("user.helpers.unconfirmedAccount")}</Tag>
                            <Popconfirm
                              title={t("user.titles.resendConfirmLinkConfirm")}
                              icon={<PopconfirmDeleteIcon />}
                              okText={t("common.yes")}
                              cancelText={t("common.no")}
                              okType="danger"
                              onConfirm={() => resendUserConfirmLink.mutate({ id1: props.clientId, id2: account.id })}
                            >
                              <SecondaryButton size="small" type="primary" icon={<AntIcon type="mail" />}>
                                {t("user.actions.resendConfirmLink")}
                              </SecondaryButton>
                            </Popconfirm>
                          </>
                        )}
                      </Space>
                    </td>
                  </tr>
                  <tr>
                    <td>{t("user.attrs.phone")}:</td>
                    <td>
                      {account.confirmed
                        ? account.user.phone && formatPhoneNumber(account.user.phone)
                        : account.user.phoneToConfirm && formatPhoneNumber(account.user.phoneToConfirm)}
                    </td>
                  </tr>
                </tbody>
              </table>
            ),
          }))}
        />
      ) : undefined}

      <ActionButton icon="plus" className="margin-top-small" onClick={() => setCreateFormOpen(true)}>
        {t("user.actions.addAccount")}
      </ActionButton>

      <CreateClientUserAccountForm
        open={createFormOpen || addAccountRequested}
        clientId={props.clientId}
        onFormCancel={handleCreateFormCancel}
      />

      <UpdateClientUserAccountForm
        clientId={props.clientId}
        account={accountToUpdate}
        onFormCancel={() => setAccountToUpdate(undefined)}
      />
    </>
  );
};
